import React, { useState, useEffect } from "react";
import "./tpl.css";
import "./Company.css";
import Sidebar from "../Sidebar";
import Header from "../Header";
import axios from "axios";
import API_BASE_URL from "../../config";
import { BsClipboard, BsCheckCircleFill, BsXCircleFill, BsCheck2 } from "react-icons/bs";

export default function Company() {
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({});
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const token = localStorage.getItem("userToken");
        if (!token) { window.location.href = "/login"; return; }
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_BASE_URL}/company`, { headers });
        setCompany(res.data);
        setFormData(res.data);
        setUserRole(res.data.role);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching company:", err.response?.data || err.message);
        setError("Failed to load company details.");
        setLoading(false);
      }
    };
    fetchCompanyData();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_BASE_URL}/company/${company.id}`, formData, { headers });
      setCompany({ ...company, ...formData });
      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error("Error updating company:", err.response?.data || err.message);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(company.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isAdmin = userRole === "admin";
  const hasChanges = company && (
    formData.name !== company.name || formData.address !== company.address ||
    formData.city !== company.city || formData.state !== company.state ||
    formData.postal_code !== company.postal_code || formData.country !== company.country
  );

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="page-header dash-head">
            <div>
              <h4>Company</h4>
              <p className="page-subtitle">Your company profile.</p>
            </div>
            {isAdmin && !loading && !error && (
              <button className="tpl-cta" onClick={handleSave} disabled={!hasChanges || saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            )}
          </div>

          {loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading company…</p></div>
          ) : error ? (
            <div className="page-error"><BsXCircleFill size={32} /><p>{error}</p></div>
          ) : (
            <div className="tpl-card" style={{ maxWidth: 720 }}>
              <div className="tpl-card-h"><h6>General</h6></div>
              <div className="tpl-fields">
                <div className="tpl-field">
                  <label>Company ID</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input className="tpl-input mono" value={company.id} readOnly />
                    <button className="tpl-ic" onClick={copyToClipboard} title="Copy Company ID" style={{ margin: 0 }}>
                      {copied ? <BsCheck2 /> : <BsClipboard />}
                    </button>
                  </div>
                </div>
                <div className="tpl-field">
                  <label>Company name</label>
                  <input name="name" value={formData.name || ""} onChange={handleChange} disabled={!isAdmin} placeholder="Enter company name" />
                </div>
                <div className="tpl-field">
                  <label>Address</label>
                  <input name="address" value={formData.address || ""} onChange={handleChange} disabled={!isAdmin} placeholder="Street address" />
                </div>
                <div className="tpl-fgrid">
                  <div className="tpl-field">
                    <label>City</label>
                    <input name="city" value={formData.city || ""} onChange={handleChange} disabled={!isAdmin} placeholder="City" />
                  </div>
                  <div className="tpl-field">
                    <label>State / Province</label>
                    <input name="state" value={formData.state || ""} onChange={handleChange} disabled={!isAdmin} placeholder="State" />
                  </div>
                </div>
                <div className="tpl-fgrid">
                  <div className="tpl-field">
                    <label>Postal code</label>
                    <input name="postal_code" value={formData.postal_code || ""} onChange={handleChange} disabled={!isAdmin} placeholder="Postal code" />
                  </div>
                  <div className="tpl-field">
                    <label>Country</label>
                    <input name="country" value={formData.country || ""} onChange={handleChange} disabled={!isAdmin} placeholder="Country" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {saveStatus && (
        <div className={`toast-notification ${saveStatus === "success" ? "toast-success" : "toast-error"}`}>
          {saveStatus === "success" ? <BsCheckCircleFill /> : <BsXCircleFill />}
          <span>{saveStatus === "success" ? "Company updated successfully!" : "Failed to save. Please try again."}</span>
        </div>
      )}
    </div>
  );
}
